<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';

// Aquecimento de chip novo (anti-ban Fase 2A): número novo que dispara volume
// alto no primeiro dia é banido. A curva sobe sozinha em 21 dias.
const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();

const isSaving = ref(false);
const warmup = computed(() => props.inbox?.warmup || {});
const health = computed(() => props.inbox?.health || {});
const isActive = computed(() => !!warmup.value.active);
// Freado por degradação (Fase 2B): o número perdeu teto por saúde, não por
// ser novo. Os dois podem valer ao mesmo tempo; o teto efetivo é o menor.
const isThrottled = computed(() => !!health.value.throttled);
const hasLimit = computed(() => isActive.value || isThrottled.value);

const usage = computed(() => ({
  sent: warmup.value.sent_today || 0,
  limit: warmup.value.effective_limit || warmup.value.daily_limit || 0,
}));

const progressPercent = computed(() => {
  const { sent, limit } = usage.value;
  if (!limit) return 0;
  return Math.min(100, Math.round((sent / limit) * 100));
});

const statusLabel = computed(() => {
  if (isThrottled.value) return t('INBOX_MGMT.WARMUP.THROTTLED_STATUS');
  if (isActive.value) {
    return t('INBOX_MGMT.WARMUP.ACTIVE_STATUS', {
      day: warmup.value.day,
      total: warmup.value.duration_days,
    });
  }
  return warmup.value.skipped
    ? t('INBOX_MGMT.WARMUP.SKIPPED_STATUS')
    : t('INBOX_MGMT.WARMUP.INACTIVE_STATUS');
});

const run = async operation => {
  isSaving.value = true;
  try {
    await InboxesAPI.warmup(props.inbox.id, operation);
    emit('updated');
  } catch {
    useAlert(t('INBOX_MGMT.WARMUP.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h4 class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          <span class="i-lucide-flame size-4 text-n-amber-11" />
          {{ t('INBOX_MGMT.WARMUP.TITLE') }}
        </h4>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.WARMUP.DESCRIPTION') }}
        </p>
      </div>
      <span
        class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
        :class="
          isThrottled
            ? 'bg-n-ruby-3 text-n-ruby-11'
            : isActive
              ? 'bg-n-amber-3 text-n-amber-11'
              : 'bg-n-alpha-2 text-n-slate-11'
        "
        data-testid="warmup-status"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div v-if="hasLimit" class="mt-3">
      <div class="mb-1 flex justify-between text-xs text-n-slate-11">
        <span>
          {{
            t('INBOX_MGMT.WARMUP.USAGE', {
              sent: usage.sent,
              limit: usage.limit,
            })
          }}
        </span>
        <span>{{ `${progressPercent}%` }}</span>
      </div>
      <div class="h-1.5 w-full overflow-hidden rounded-full bg-n-alpha-2">
        <div
          class="h-full rounded-full bg-n-amber-9 transition-all"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
    </div>

    <p
      v-if="isThrottled && health.reason"
      class="mt-3 rounded-lg bg-n-ruby-2 px-3 py-2 text-xs text-n-ruby-11"
      data-testid="warmup-throttle-reason"
    >
      {{ t('INBOX_MGMT.WARMUP.THROTTLE_REASON', { reason: health.reason }) }}
    </p>

    <div class="mt-4 flex gap-2">
      <Button
        v-if="isActive"
        variant="outline"
        color="slate"
        size="sm"
        :is-loading="isSaving"
        data-testid="warmup-skip"
        :label="t('INBOX_MGMT.WARMUP.SKIP')"
        @click="run('skip')"
      />
      <Button
        v-else
        variant="outline"
        color="amber"
        size="sm"
        :is-loading="isSaving"
        data-testid="warmup-start"
        :label="t('INBOX_MGMT.WARMUP.START')"
        @click="run('start')"
      />
    </div>
  </div>
</template>
